import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, PageHead, useToast } from '@w7/ui'
import { sessionApi } from '@/api/sessionApi'
import { getUserProfile } from '@/auth/session'
import { parsePlResponse, type PlRow, type PlTotals } from './plDetailsUtils'

type EventOpt = { _id: string; combineName?: string; name?: string; betfairId?: string | number }
type UserOpt = { _id: string; userName?: string }
type Row = PlRow
type Totals = PlTotals

const PRESET_USERS: UserOpt[] = [
  { userName: 'All', _id: 'all' },
  { userName: 'Sky', _id: 'sky' },
  { userName: 'Parker', _id: 'parker' },
]

export function PlDetailsPage() {
  const toast = useToast()
  const navigate = useNavigate()
  const [events, setEvents] = useState<EventOpt[]>([])
  const [users, setUsers] = useState<UserOpt[]>(PRESET_USERS)
  const [accessLevel, setAccessLevel] = useState('')
  const [eventId, setEventId] = useState('')
  const [userId, setUserId] = useState('all')
  const [rows, setRows] = useState<Row[]>([])
  const [totals, setTotals] = useState<Totals>({})

  useEffect(() => {
    void sessionApi
      .getAllEvents()
      .then((res) => {
        const data = res.data as EventOpt[] | { data?: EventOpt[] }
        const list = Array.isArray(data) ? data : (data?.data ?? [])
        const mapped = (Array.isArray(list) ? list : []).map((ev) => ({
          ...ev,
          combineName: `${ev.name ?? ''}-${ev.betfairId ?? ''}`,
        }))
        setEvents(mapped)
      })
      .catch(() => toast.error('Failed to load events'))
    void getUserProfile().then((u) => {
      const level = u?.accessLevel ?? ''
      setAccessLevel(level)
      if (level === 'admin') {
        void sessionApi
          .getAllUsers()
          .then((res) => {
            const data = res.data as UserOpt[] | { data?: UserOpt[] }
            const list = Array.isArray(data) ? data : (data?.data ?? [])
            setUsers([...PRESET_USERS, ...(Array.isArray(list) ? list : [])])
          })
          .catch(() => setUsers(PRESET_USERS))
      }
    })
  }, [toast])

  const selectedEvent = () => events.find((e) => e._id === eventId) ?? null
  const selectedUser = () => users.find((u) => u._id === userId) ?? PRESET_USERS[0]

  const loadPl = (nextEventId = eventId, nextUserId = userId) => {
    const event = events.find((e) => e._id === nextEventId)
    if (!event) {
      setRows([])
      setTotals({})
      return
    }
    const user =
      accessLevel === 'admin'
        ? (users.find((u) => u._id === nextUserId) ?? PRESET_USERS[0])
        : PRESET_USERS[0]
    void sessionApi
      .filterEventsForDropdown({ event, selectedUser: user })
      .then((res) => {
        const { rows: nextRows, totals: nextTotals } = parsePlResponse(res.data)
        setRows(nextRows)
        setTotals(nextTotals)
      })
      .catch(() => {
        setRows([])
        setTotals({})
        toast.error('Failed to load PL details')
      })
  }

  const download = () => {
    const event = selectedEvent()
    if (!event) {
      toast.error('Select an event')
      return
    }
    void sessionApi
      .downloadPLdetails({
        event,
        selectedUser: accessLevel === 'admin' ? selectedUser() : PRESET_USERS[0],
      })
      .then(() => toast.success('Download started'))
      .catch(() => toast.error('Download failed'))
  }

  return (
    <div>
      <PageHead
        title="PL Details"
        onBack={() => navigate('/sessionmarket')}
        actions={
          <Button variant="primary" onClick={download}>
            Download
          </Button>
        }
      />
      <div className="sm-row" style={{ marginBottom: 12 }}>
        <strong className="sm-market-title">Event List</strong>
        <select
          className="w7-input"
          value={eventId}
          onChange={(e) => {
            const id = e.target.value
            setEventId(id)
            loadPl(id, userId)
          }}
        >
          <option value="">Select Events</option>
          {events.map((ev) => (
            <option key={ev._id} value={ev._id}>
              {ev.combineName ?? ev.name}
            </option>
          ))}
        </select>
        {accessLevel === 'admin' ? (
          <select
            className="w7-input"
            value={userId}
            onChange={(e) => {
              const id = e.target.value
              setUserId(id)
              loadPl(eventId, id)
            }}
          >
            {users.map((u) => (
              <option key={u._id} value={u._id}>
                {u.userName}
              </option>
            ))}
          </select>
        ) : null}
      </div>
      <div className="sm-card">
        <table className="w7-table">
          <thead>
            <tr>
              <th>Sr No</th>
              <th>Market</th>
              <th>Created By</th>
              <th>Win Amount</th>
              <th>Commission</th>
              <th>Net Amount</th>
            </tr>
          </thead>
          <tbody>
            {!rows.length ? (
              <tr>
                <td colSpan={6}>No Data found</td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr key={r._id ?? i}>
                  <td>{i + 1}</td>
                  <td>{r._id}</td>
                  <td>{r.userName}</td>
                  <td>{r.companyMasterWinInr}</td>
                  <td>{r.companyMasterCommissionInr}</td>
                  <td>{r.companyMasterNetInr}</td>
                </tr>
              ))
            )}
            <tr>
              <td />
              <td />
              <td />
              <td>{totals.companyMasterWinInrSum}</td>
              <td>{totals.companyMasterCommissionInrSum}</td>
              <td>{totals.companyMasterNetInrSum}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}
